import React, { useState, useEffect, useMemo } from "react";
import {
  GetApprovedLeadTransferReq,
  GetRejectedLeadTransferReq,
} from "../Services/RecruitmentService";
import { User, Mail, Phone, Copy, Check } from "lucide-react";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";
import { formatDateTimeU } from "../../../../Utils/FormUtils";

const LeadTransferProcessedRequests = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRequests, setTotalRequests] = useState(0);
  const [alert, setAlert] = useState(null);
  const [requestType, setRequestType] = useState("approved");
  const [copiedPhone, setCopiedPhone] = useState(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      let res;
      if (requestType === "approved") {
        res = await GetApprovedLeadTransferReq(page, pageSize);
        setRequests(res?.leadTransferReqs || []);
      } else {
        res = await GetRejectedLeadTransferReq(page, pageSize);
        setRequests(res?.leadTransferReqs || []);
      }
      setTotalRequests(res?.totalRequests || 0);
    } catch (error) {
      setAlert({
        type: "error",
        message:
          error?.message || "Failed to fetch requests. Please try again later.",
      });
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [page, pageSize, requestType]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalRequests / pageSize);
  }, [totalRequests, pageSize]);

  const handleCopyPhone = async (phone) => {
    if (!phone) return;

    try {
      await navigator.clipboard.writeText(phone);
      setCopiedPhone(phone);
      setTimeout(() => setCopiedPhone(null), 1500);
    } catch (error) {
      setAlert({
        type: "error",
        message: "Failed to copy phone number. Please copy manually.",
      });
    }
  };

  return (
    <>
      <div className="p-2 space-y-3">
        {/* TOOLBAR */}

        <div className="max-w-8xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-sm px-5 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Showing</span>

                <select
                  value={requestType}
                  onChange={(e) => {
                    setPage(1);

                    setRequestType(e.target.value);
                  }}
                  className="h-9 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                >
                  <option value="approved">Approved</option>

                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-5">
              <div className="text-sm text-slate-600">
                Showing
                <span className="font-semibold">
                  {" "}
                  {totalRequests === 0
                    ? "0"
                    : `${(page - 1) * pageSize + 1}-${Math.min(
                        page * pageSize,
                        totalRequests,
                      )}`}
                </span>{" "}
                of <span className="font-semibold">{totalRequests}</span>
              </div>

              <div className="flex items-center gap-2 pl-4 border-l border-slate-200">
                <span className="text-sm text-slate-500">Rows</span>

                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);

                    setPageSize(Number(e.target.value));
                  }}
                  className="h-8 px-2 rounded-lg border border-slate-200 bg-slate-50 text-sm"
                >
                  <option value={10}>10</option>

                  <option value={20}>20</option>

                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE */}

        <div className="max-w-8xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            <div className="col-span-2 pl-12">Lead</div>
            <div className="col-span-2 pl-4">Phone</div>
            <div className="col-span-2">Requested By</div>
            <div className="col-span-2">Owner</div>
            <div className="col-span-1">
              {requestType === "approved" ? "Approved By" : "Rejected By"}
            </div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1">Remark</div>
            <div className="col-span-1 pl-1">Requested Date</div>
          </div>

          <div className="divide-y divide-slate-100">
            {loading && initialLoading ? (
              <div className="py-16 text-center text-sm text-slate-500">
                Loading requests...
              </div>
            ) : requests.length === 0 ? (
              <div className="py-16 flex flex-col items-center">
                <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                  <User size={22} className="text-slate-400" />
                </div>

                <h3 className="text-sm font-semibold text-slate-700">
                  No requests found
                </h3>
              </div>
            ) : (
              requests.map((item) => {
                const requested = formatDateTimeU(item.requestedAt, { split: true });

                const updated = formatDateTimeU(item.lastUpdatedDate, { split: true });
                const statusLabel =
                  item.statusText === "Approve"
                    ? "Approved"
                    : item.statusText === "Reject"
                      ? "Rejected"
                      : item.statusText;
                const statusClass =
                  item.status === "1"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                    : "bg-red-50 text-red-700 border-red-100";
                return (
                  <div
                    key={item.requestUID}
                    className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-blue-50/40 transition-all"
                  >
                    {/* LEAD */}
                    <div className="col-span-2 flex gap-3">
                      <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center font-semibold text-blue-700 shrink-0">
                        {item.leadName?.charAt(0)}
                      </div>

                      <div>
                        <p className="font-semibold text-sm text-slate-800">
                          {item.leadName}
                        </p>

                        <div className="flex items-center gap-2  text-xs text-slate-500">
                          <span>{item.email}</span>
                        </div>
                      </div>
                    </div>

                    {/* PHONE */}
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <Phone size={13} className="text-slate-400" />

                          <span
                            className="font-medium tracking-wide"
                            title={item.phoneNo}
                          >
                            {item.phoneNo || "-"}
                          </span>
                        </div>

                        {item.phoneNo && (
                          <button
                            type="button"
                            onClick={() => handleCopyPhone(item.phoneNo)}
                            className={`inline-flex h-6 w-6 items-center justify-center rounded-lg border transition-all
                                        ${
                                          copiedPhone === item.phoneNo
                                            ? "border-emerald-200 bg-emerald-50 text-emerald-600"
                                            : "border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                                        }
                                      `}
                            title={
                              copiedPhone === item.phoneNo
                                ? "Copied"
                                : "Copy phone number"
                            }
                          >
                            {copiedPhone === item.phoneNo ? (
                              <Check size={12} />
                            ) : (
                              <Copy size={12} />
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* REQUESTED BY */}
                    <div className="col-span-2">
                      <p className="text-sm font-medium">
                        {item.requestedByName}
                      </p>

                      <p className="text-xs text-slate-500">
                        {item.requestedByCode}
                      </p>
                    </div>

                    {/* CURRENT OWNER */}
                    <div className="col-span-2">
                      <p className="text-sm font-medium">
                        {item.currentOwnerName}
                      </p>

                      <p className="text-xs text-slate-500">
                        {item.currentOwnerCode}
                      </p>
                    </div>

                    {/* Action By */}
                    <div className="col-span-1">
                      <p className="text-sm font-medium">
                        {item.updatedByName || "-"}
                      </p>

                      <p className="text-xs text-slate-500">
                        {item.updatedByCode || "-"}
                      </p>
                    </div>

                    {/* STATUS */}
                    <div className="col-span-1">
                      <span className={`inline-flex px-2 py-1 rounded-lg border text-xs font-medium ${statusClass}`}>
                        {statusLabel}
                      </span>
                    </div>

                    {/* REMARK */}
                    <div className="col-span-1">
                      <p className="text-sm text-slate-700 break-words">
                        {item.remark || "-"}
                      </p>
                    </div>

                    <div className="col-span-1">
                      <div className="flex flex-col items-start">
                        <p className="text-sm">{requested.date}</p>

                        <p className="text-xs text-slate-400">
                          {requested.time}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* SAME PAGINATION */}

          {!initialLoading && totalPages >= 1 && (
            <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 bg-white">
              <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((prev) => prev - 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all
                              ${
                                page === 1
                                  ? "text-slate-300 cursor-not-allowed"
                                  : "text-slate-500 hover:bg-slate-100"
                              }`
                            }
                >
                  Prev
                </button>

                {(() => {
                  const pages = [];

                  for (let i = 1; i <= totalPages; i++) {
                    if (
                      i === 1 ||
                      i === totalPages ||
                      (i >= page - 1 && i <= page + 1)
                    ) {
                      pages.push(i);
                    }
                  }

                  return pages.map((pageNumber, index) => {
                    const prevPage = pages[index - 1];

                    return (
                      <React.Fragment key={pageNumber}>
                        {prevPage && pageNumber - prevPage > 1 && (
                          <span className="px-1 text-sm text-slate-600">
                            ...
                          </span>
                        )}

                        <button
                          onClick={() => setPage(pageNumber)}
                          className={`h-8 min-w-[34px] px-2 rounded-lg text-sm font-semibold transition-all
                                      ${
                                        page === pageNumber
                                          ? "bg-primary text-white shadow-sm"
                                          : "text-slate-600 hover:bg-slate-100"
                                      }
                                    `}
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  });
                })()}

                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((prev) => prev + 1)}
                  className={`h-8 px-3 rounded-lg text-sm font-medium transition-all
                              ${
                                page === totalPages
                                  ? "text-slate-300 cursor-not-allowed"
                                  : "text-slate-500 hover:bg-slate-100"
                              }
                            `}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert?.type}
          message={alert?.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default LeadTransferProcessedRequests;
